<script lang="ts">
  import type { Api, OrgSummary } from "../lib/api";
  import OrgDetail from "./OrgDetail.svelte";
  import Card from "../lib/ui/Card.svelte";
  import Field from "../lib/ui/Field.svelte";
  import Button from "../lib/ui/Button.svelte";
  import Spinner from "../lib/ui/Spinner.svelte";
  import Banner from "../lib/ui/Banner.svelte";
  import EmptyState from "../lib/ui/EmptyState.svelte";
  import Pill from "../lib/ui/Pill.svelte";
  import { seg, navigate } from "../lib/route.svelte";
  import { Checkbox, Label } from "@price-check/ui";

  let { api }: { api: Api } = $props();

  let orgs = $state<OrgSummary[]>([]);
  let loading = $state(true);
  let error = $state("");
  let newName = $state("");
  let adminEmail = $state("");
  let adminPassword = $state("");
  let enableAll = $state(false);
  let createError = $state("");
  const selected = $derived(seg(1));

  async function load() {
    loading = true;
    error = "";
    try {
      orgs = await api.listOrgs();
    } catch (e) {
      error = e instanceof Error ? e.message : "failed to load";
    } finally {
      loading = false;
    }
  }
  $effect(() => {
    void load();
  });

  async function create(e: Event) {
    e.preventDefault();
    const name = newName.trim();
    const email = adminEmail.trim().toLowerCase();
    if (!name || !email) return;
    createError = "";
    try {
      const r = await api.onboardOrg({
        name,
        adminEmail: email,
        adminPassword: adminPassword.trim() || undefined,
        enableAllVendors: enableAll,
      });
      newName = "";
      adminEmail = "";
      adminPassword = "";
      enableAll = false;
      navigate(`/orgs/${r.org.id}`);
    } catch (err) {
      createError = err instanceof Error ? err.message : "could not create the customer";
    }
  }
</script>

{#if selected}
  <OrgDetail
    {api}
    orgId={selected}
    onClose={() => {
      navigate("/orgs");
      void load();
    }}
  />
{:else}
  <div class="flex flex-col gap-5">
    <Card
      title="New customer"
      subtitle="Creates the organization + its admin user in one step (invited by email when no password is set)."
    >
      <form class="flex flex-col gap-2.5" onsubmit={create}>
        <div class="flex flex-wrap gap-2.5">
          <div class="flex-1 min-w-40">
            <Field placeholder="Organization name" bind:value={newName} />
          </div>
          <div class="flex-1 min-w-40">
            <Field placeholder="Admin email" bind:value={adminEmail} />
          </div>
          <div class="flex-1 min-w-40">
            <Field
              type="password"
              placeholder="Admin password (invite if blank)"
              bind:value={adminPassword}
            />
          </div>
        </div>
        <div class="flex items-center gap-2.5">
          <Label class="flex items-center gap-1.5 text-sm font-normal cursor-pointer">
            <Checkbox bind:checked={enableAll} aria-label="Enable legacy global suppliers" />
            Enable legacy global suppliers
          </Label>
          <span class="ml-auto">
            <Button type="submit" disabled={!newName.trim() || !adminEmail.trim()}>+ Create</Button>
          </span>
        </div>
        {#if createError}<Banner tone="danger">{createError}</Banner>{/if}
      </form>
    </Card>

    {#if loading}
      <Spinner label="Loading organizations…" />
    {:else if error}
      <Banner tone="danger">{error}</Banner>
    {:else if orgs.length === 0}
      <Card pad={false}>
        <EmptyState
          icon="▦"
          title="No organizations yet"
          hint="Create your first organization with the form above."
        />
      </Card>
    {:else}
      <Card title="Organizations" pad={false}>
        <table class="data-table">
          <thead>
            <tr>
              <th>Organization</th>
              <th class="text-center! w-27.5">Plan</th>
              <th class="text-center! w-27.5">Members</th>
              <th class="text-center! w-27.5">Suppliers</th>
              <th class="text-right!">Manage</th>
            </tr>
          </thead>
          <tbody>
            {#each orgs as o (o.id)}
              <tr>
                <td class="font-bold">{o.name}</td>
                <td class="text-center! w-27.5">
                  {#if o.plan === "paid"}
                    <Pill tone="ok">paid</Pill>
                  {:else if o.trial_ends_at && Date.parse(o.trial_ends_at) <= Date.now()}
                    <Pill tone="amber">trial expired</Pill>
                  {:else}
                    <Pill tone="slate">trial</Pill>
                  {/if}
                </td>
                <td class="text-center! w-27.5 text-muted">{o.member_count}</td>
                <td class="text-center! w-27.5 text-muted">{o.config_count}</td>
                <td class="text-right!">
                  <Button variant="secondary" size="sm" onclick={() => navigate(`/orgs/${o.id}`)}>
                    Manage →
                  </Button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </Card>
    {/if}
  </div>
{/if}
